/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Text from '@Text';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cpcCardConfig from '../../Data/Maps/cpcCardConfig';
import PreloadStatus from '../../Data/PreloadStatus';
import {
  setPreloadStatus,
  setShowCpcRecommendations,
  setWasPreviousPageCpcCard,
} from '../../Lib/redux/slices/cpcCard';
import { updateRecalculateRequiredWidgetHeight } from '../../Lib/redux/slices/dynamicHeight';
import { setPendingMoveForwards } from '../../Lib/redux/slices/navigationControls';
import {
  areConditionalQuestionsAnswered,
  getEnvConfigForFormId,
} from '../__helpers__/component.helpers';
import { shouldShowCpcCardOnThisPage } from './cpcCard.helpers';
import makeCpcCardStyling from './makeCpcCardStyling';
import Html from '../Html/Html';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const CpcCard = () => {
  const { currentPage } = useSelector((store) => store.navigation);
  const { enterPhase, exitPhase } = useSelector((store) => store.transition);
  const { preloadStatus, showCpcRecommendations, wasPreviousPageCpcCard } =
    useSelector((store) => store.cpcCard);
  const components = useSelector((store) => store.components);
  const { isDynamicHeightEnabled } = useSelector(
    (store) => store.optionalFormConfig
  );
  const isBounceNavigationAnimationEnabled = useSelector(
    (store) => store.features.isBounceNavigationAnimationEnabled
  );
  const { pageUrl, formId } = useSelector((store) => store.formConfig);
  const cheqData = useSelector((store) => store.userIdentities.cheqData);
  const iframeRef = useRef();
  const cpcCardRef = useRef();
  const dispatch = useDispatch();
  const containerRef = useRef();
  const theme = useTheme();

  const textExperimentName = useGetExperimentName({
    componentName: 'Text',
  });

  const styling = makeCpcCardStyling({
    iframeLoaded: preloadStatus === PreloadStatus.COMPLETED,
    showCpcRecommendations,
    theme,
  });

  const envCpcCardConfigForForm = getEnvConfigForFormId({
    config: cpcCardConfig,
    formId,
  });

  useEffect(() => {
    if (preloadStatus === PreloadStatus.WAITING_FOR_START) {
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.IN_PROGRESS,
        })
      );

      const errorTimeout = setTimeout(() => {
        // If the cpc card hasn't loaded within 7 seconds something has gone wrong, cancel the request so the user isn't stuck
        if (preloadStatus !== PreloadStatus.COMPLETED) {
          dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: false }));
          dispatch(
            setPreloadStatus({
              preloadStatus: PreloadStatus.NOT_STARTED,
            })
          );
        }
      }, 7000);

      if (iframeRef.current) {
        iframeRef.current.src = envCpcCardConfigForForm.cardUrl;

        iframeRef.current.addEventListener('load', () => {
          dispatch(
            setPreloadStatus({
              preloadStatus: PreloadStatus.COMPLETED,
            })
          );

          clearTimeout(errorTimeout);

          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({
              type: 'setPalette',
              palette: {
                ...theme.mvf.palette,
              },
            }),
            '*'
          );
          if (pageUrl) {
            iframeRef.current.contentWindow.postMessage(
              JSON.stringify({
                type: 'setParentUrl',
                url: pageUrl,
              }),
              '*'
            );
          }
        });

        window.addEventListener('message', (event) => {
          if (!event.data || typeof event.data !== 'string') {
            return;
          }

          const { data } = event;

          if (data.includes('resize-iframe')) {
            const { width, height } = JSON.parse(data);

            if (iframeRef.current && width && height) {
              iframeRef.current.style.height = `${height}px`;

              if (
                isDynamicHeightEnabled === true &&
                height > 0 &&
                showCpcRecommendations === true
              ) {
                dispatch(
                  updateRecalculateRequiredWidgetHeight({
                    isWidgetHeightRecalculationRequired: true,
                  })
                );
              }
            }
          }
        });
      }
    }
  }, [preloadStatus]);

  useEffect(() => {
    if (
      cheqData &&
      iframeRef.current &&
      preloadStatus === PreloadStatus.COMPLETED
    ) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          type: 'setCheqData',
          cheqData,
        }),
        '*'
      );
    }
  }, [cheqData, preloadStatus]);

  useEffect(() => {
    return () => {
      // On component unmount, reset the statuses so that it can load again when needed
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.NOT_STARTED,
        })
      );
      dispatch(
        setShowCpcRecommendations({
          showCpcRecommendations: false,
        })
      );
    };
  }, [dispatch]);

  useEffect(() => {
    if (enterPhase === true) {
      /**
       * If we are entering the page, check if we should show the CPC card and add the styles
       */
      const answersMatchCpcCardResult = areConditionalQuestionsAnswered({
        formId,
        components,
        config: cpcCardConfig,
      });

      const showCpcCardWithTransitionResult = shouldShowCpcCardOnThisPage(
        formId,
        components,
        currentPage
      );

      let transitionAnimationBaseClassPrefix;
      if (isBounceNavigationAnimationEnabled) {
        transitionAnimationBaseClassPrefix = 'bounce-cpc-card-direction';
      } else {
        transitionAnimationBaseClassPrefix = 'transition-cpc-card-direction';
      }

      if (showCpcCardWithTransitionResult && answersMatchCpcCardResult) {
        containerRef.current.classList.add(
          `${transitionAnimationBaseClassPrefix}-enter-active`
        );
        containerRef.current.classList.remove(
          `${transitionAnimationBaseClassPrefix}-exit-active`
        );
      }

      dispatch(
        setShowCpcRecommendations({
          showCpcRecommendations:
            answersMatchCpcCardResult && showCpcCardWithTransitionResult,
        })
      );
    }

    if (
      exitPhase === false &&
      showCpcRecommendations === true &&
      isDynamicHeightEnabled === true
    ) {
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterPhase]);

  useEffect(() => {
    if (exitPhase === true) {
      if (showCpcRecommendations === true && wasPreviousPageCpcCard !== true) {
        // we are now leaving the CPC card page, this is important for the <Transition /> component to know
        dispatch(setWasPreviousPageCpcCard({ wasPreviousPageCpcCard: true }));
      } else if (wasPreviousPageCpcCard !== false) {
        dispatch(setWasPreviousPageCpcCard({ wasPreviousPageCpcCard: false }));
      }

      const answersMatchCpcCardResult = areConditionalQuestionsAnswered({
        formId,
        components,
        config: cpcCardConfig,
      });

      let transitionAnimationBaseClassPrefix;
      if (isBounceNavigationAnimationEnabled) {
        transitionAnimationBaseClassPrefix = 'bounce-cpc-card-direction';
      } else {
        transitionAnimationBaseClassPrefix = 'transition-cpc-card-direction';
      }

      if (answersMatchCpcCardResult) {
        containerRef.current.classList.add(
          `${transitionAnimationBaseClassPrefix}-exit-active`
        );
        containerRef.current.classList.remove(
          `${transitionAnimationBaseClassPrefix}-enter-active`
        );
      }
    }

    if (exitPhase === false && wasPreviousPageCpcCard === true) {
      // We are exiting the CPC card by the user navigating backwards.
      // Set the status back to NOT_STARTED so it can load again next time
      // This guards against a user navigating backwards and forwards due to a networking error,
      // which would mean the user doesn't get stuck with broken styles
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.NOT_STARTED,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitPhase]);

  useEffect(() => {
    if (preloadStatus === PreloadStatus.COMPLETED) {
      // Unmounts and remounts are causing the ref to be null, so re-apply the card URL if this happens
      dispatch(
        setPreloadStatus({
          preloadStatus: PreloadStatus.NOT_STARTED,
        })
      );
    }
  }, [iframeRef.current]);

  /**
   * If there is no envCpcCardConfigForForm for this form we don't need to render the CPC card or run any effects
   */
  if (!envCpcCardConfigForForm) {
    return null;
  }

  return (
    <div ref={containerRef} className="cpcCardTransitionContainer">
      <Grid css={styling.grid} container direction="column">
        <Container
          css={styling.container}
          maxWidth="md"
          ref={cpcCardRef}
          data-testid="cpcCardContentContainer"
        >
          <Text variant="question" experimentName={textExperimentName}>
            {envCpcCardConfigForForm.title}
          </Text>
          {envCpcCardConfigForForm.subheading && (
            <Html text={envCpcCardConfigForForm.subheading} />
          )}
          <div css={styling.iframeContainer}>
            <iframe
              data-testid="cpcCardContentIframe"
              css={styling.iframe}
              ref={iframeRef}
              title="CPC Card"
              sandbox="allow-top-navigation allow-scripts"
            />
          </div>
        </Container>
      </Grid>
    </div>
  );
};

export default CpcCard;
